import { useState } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface BroadcastDateRangePickerProps {
    /** YYYY-MM-DD no calendário local, ou vazio = sem recorte. */
    dateFrom?: string;
    dateTo?: string;
    onChange: (from?: string, to?: string) => void;
}

const paraISO = (d: Date): string => {
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
};

const paraData = (s?: string): Date | undefined => {
    if (!s) return undefined;
    const [ano, mes, dia] = s.split('-').map(Number);
    if (!ano || !mes || !dia) return undefined;
    return new Date(ano, mes - 1, dia);
};

const diasAtras = (n: number): Date => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
};

const ATALHOS: { rotulo: string; dias: number }[] = [
    { rotulo: 'Hoje', dias: 0 },
    { rotulo: 'Últimos 7 dias', dias: 6 },
    { rotulo: 'Últimos 30 dias', dias: 29 },
    { rotulo: 'Últimos 90 dias', dias: 89 },
];

/**
 * Um calendário só, uma requisição só, e por cima do card.
 *
 * Duas coisas que a versão anterior errava:
 *
 * 1. Dois `<input type="date">` independentes disparavam a busca no primeiro
 *    campo preenchido — com o intervalo pela metade. Aqui o `onChange` só sai
 *    quando existe começo E fim.
 * 2. O popover era `absolute` dentro do painel de filtros, que tem
 *    `overflow-hidden` (o card usa isso para animar a abertura). O calendário
 *    nascia recortado. Radix Popover renderiza em portal, fora da árvore do
 *    card, então nada o corta.
 */
export default function BroadcastDateRangePicker({ dateFrom, dateTo, onChange }: BroadcastDateRangePickerProps) {
    const [aberto, setAberto] = useState(false);
    const [inicioParcial, setInicioParcial] = useState<Date | undefined>(undefined);

    const de = paraData(dateFrom);
    const ate = paraData(dateTo);
    const temRecorte = Boolean(de && ate);

    const fechar = () => {
        setAberto(false);
        setInicioParcial(undefined);
    };

    const aplicar = (from: Date, to: Date) => {
        const [ini, fim] = from <= to ? [from, to] : [to, from];
        onChange(paraISO(ini), paraISO(fim));
        fechar();
    };

    const clicarDia = (dia: Date) => {
        if (!inicioParcial) {
            setInicioParcial(dia);
            return;
        }
        aplicar(inicioParcial, dia);
    };

    const limpar = () => {
        onChange(undefined, undefined);
        fechar();
    };

    const rotulo = temRecorte
        ? `${format(de!, 'dd/MM/yy', { locale: ptBR })} – ${format(ate!, 'dd/MM/yy', { locale: ptBR })}`
        : 'Todo o período';

    return (
        <Popover open={aberto} onOpenChange={(v) => (v ? setAberto(true) : fechar())}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border px-3 text-sm transition-colors ${temRecorte
                        ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 text-gray-900'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}
                >
                    <span className="flex items-center gap-2 truncate">
                        <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="truncate font-medium">{rotulo}</span>
                    </span>
                    <span className="flex items-center gap-1">
                        {temRecorte && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label="Limpar período"
                                onClick={(e) => { e.stopPropagation(); limpar(); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); limpar(); } }}
                                className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X className="h-3.5 w-3.5" />
                            </span>
                        )}
                        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
                    </span>
                </button>
            </PopoverTrigger>

            <PopoverContent align="start" sideOffset={6} className="w-auto rounded-2xl border-gray-200 p-0 shadow-xl">
                <div className="flex">
                    <div className="flex w-40 flex-col gap-1 border-r border-gray-100 p-3">
                        {ATALHOS.map(({ rotulo: r, dias }) => (
                            <button
                                key={r}
                                type="button"
                                onClick={() => aplicar(diasAtras(dias), new Date())}
                                className="rounded-lg px-2 py-1.5 text-left text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            >
                                {r}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={limpar}
                            className="mt-1 rounded-lg px-2 py-1.5 text-left text-xs font-medium text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                        >
                            Todo o período
                        </button>
                    </div>

                    <div className="p-3">
                        <div className="mb-2 text-xs text-gray-500">
                            {inicioParcial
                                ? `Início ${format(inicioParcial, 'dd/MM', { locale: ptBR })} — clique na data final`
                                : 'Clique na data inicial'}
                        </div>
                        <DayPicker
                            mode="range"
                            selected={inicioParcial
                                ? { from: inicioParcial, to: inicioParcial }
                                : (de && ate ? { from: de, to: ate } : undefined)}
                            onDayClick={clicarDia}
                            disabled={{ after: new Date() }}
                            locale={ptBR}
                            numberOfMonths={1}
                            defaultMonth={de ?? new Date()}
                            className="rdp-broadcast"
                        />
                    </div>
                </div>

                <style>{`
                    .rdp-broadcast { --rdp-cell-size: 34px; --rdp-accent-color: #0ea5e9; --rdp-background-color: #e0f2fe; font-size: 13px; margin: 0; }
                    .rdp-broadcast .rdp-day_selected { background-color: #0ea5e9; color: #fff; font-weight: 600; }
                    .rdp-broadcast .rdp-day_range_middle { background-color: #e0f2fe; color: #0f172a; }
                    .rdp-broadcast .rdp-day:hover:not(.rdp-day_selected) { background-color: #f3f4f6; }
                    .rdp-broadcast .rdp-caption_label { font-size: 13px; font-weight: 600; }
                `}</style>
            </PopoverContent>
        </Popover>
    );
}
